import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import classnames from 'classnames'
import type { primitives } from '@adc/blueprint'
import type { OnlineAuctionSegmentBidType } from '@adc/sdk/types'
import { internationalization } from '@adc/constants'
import {
  Button,
  BidConfirmationContent,
  useApplicationModals,
  useApplicationNotifications,
  NOTIFICATION_ROLES,
  constants as uiConstants,
} from '@adc/ui-adc'
import type { FeatureFlags } from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import { useListingContext } from 'common/context/listing'
import analytics from 'common/services/analytics'
import { isPendingFclOnlineDeposit } from 'common/utils/listing'
import { AccountRestrictedAlert } from '../../../../components/accountRestrictedAlert/index'

const BID_BUTTON_LABEL = 'Place Bid'
const MAX_BID_BUTTON_LABEL = internationalization.set_max_bid
const INCREASE_MAX_BID_BUTTON_LABEL = internationalization.increase_max_bid
const ADJUST_MAX_BID_BUTTON_LABEL = internationalization.adjust_max_bid

const { colorVars } = uiConstants

interface MaxBidAdjustmentConfig {
  enabled: boolean
  timeLimit: number
  minTimeLimitBeforeAuctionEnded: number
}

const getMaxBidUpdateValidationConfig = (
  featureFlags: Record<string, unknown>
) => {
  const flag = featureFlags?.['fe.resi.max-bid-adjustment-removal-config'] as
    | MaxBidAdjustmentConfig
    | undefined
  return {
    isMaxBidUpdateValidationEnabled: flag?.enabled ?? false,
    maxBidUpdateTimeLimitSeconds: flag?.timeLimit ?? 0,
    minTimeLimitBeforeAuctionEndSeconds:
      flag?.minTimeLimitBeforeAuctionEnded ?? 0,
  }
}

type AuctionUser = primitives.AuctionUser
interface BidButtonProps {
  bidAmount: number
  isMaxBiddingActive: boolean
  isUserConfirmed: boolean
  containerClassName?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleBidButtonClick: (callback: void) => void
  onClickSecondaryButton?: () => void
  hasError: boolean
  isPerformingBid: boolean
  performBid: (newBidAmount: number | null) => void
  biddingMode: OnlineAuctionSegmentBidType
  setBiddingMode: (mode: OnlineAuctionSegmentBidType) => void
  user: AuctionUser
  cleanUp: () => void
  featureFlags: FeatureFlags
  setIsAdjustMode: (value: boolean) => void
}

const BidButton = ({
  containerClassName,
  bidAmount,
  onChange,
  handleBidButtonClick,
  hasError,
  isMaxBiddingActive,
  isUserConfirmed,
  isPerformingBid,
  performBid,
  biddingMode,
  setBiddingMode,
  user,
  cleanUp,
  featureFlags,
  setIsAdjustMode,
}: BidButtonProps) => {
  const { listing, segment } = useListingContext()
  const { open, update, close } = useApplicationModals()
  const notifications = useApplicationNotifications()
  const hasMaxBid = segment.__methods__.hasValidMaxBid()

  const { required: occupancyDisclaimerRequired, acknowledged } =
    segment.__methods__.calculateOccupancyDisclaimerRequirements(listing)

  const [occupancyAcknowledged, setOccupancyAcknowledged] =
    useState(acknowledged)
  const [isRemoveView, setIsRemoveView] = useState(false)
  const [modalCanAdjustOrRemove, setModalCanAdjustOrRemove] = useState(false)

  const maxBidUpdateConfig = getMaxBidUpdateValidationConfig(featureFlags)
  const canAdjustOrRemove =
    segment.__methods__.canAdjustOrRemoveMaxBid?.(maxBidUpdateConfig) ?? false

  const isAdjustMode = modalCanAdjustOrRemove && hasMaxBid

  const resetBidAmountToZero = useCallback(() => {
    onChange({
      target: { value: String(0) },
    } as React.ChangeEvent<HTMLInputElement>)
  }, [onChange])

  const isConfirmBidButtonDisabled = useCallback(
    (isMaxBidModal: boolean) => {
      if (isRemoveView) {
        return false
      }

      const baseDisabled =
        isPerformingBid ||
        (occupancyDisclaimerRequired && !occupancyAcknowledged)

      if (isMaxBiddingActive && isMaxBidModal) {
        if (isAdjustMode) {
          const currentMaxBid =
            segment?.current_user_max_bid?.max_bid_amount || 0
          return (
            baseDisabled ||
            !bidAmount ||
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            bidAmount < (segment.__methods__.getNextBidAmount() || 0) ||
            bidAmount === currentMaxBid
          )
        }
        return (
          baseDisabled ||
          !bidAmount ||
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          bidAmount < (segment.__methods__.getNextMaxBidAmount() || 0)
        )
      }
      return baseDisabled
    },
    [
      isMaxBiddingActive,
      isPerformingBid,
      occupancyDisclaimerRequired,
      occupancyAcknowledged,
      bidAmount,
      segment.__methods__,
      segment?.current_user_max_bid?.max_bid_amount,
      isRemoveView,
      isAdjustMode,
    ]
  )

  const handleRemoveMaxBid = useCallback(async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      await segment.__methods__.deleteOnlineAuctionSegmentBid(
        (user.logged_in && user.party_id) || ''
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      notifications.toast.open({
        title: internationalization.max_bid_removed_success,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        role: NOTIFICATION_ROLES.SUCCESS,
      })
      analytics.maxBid.trackRemoveAutoBidConfirm.success(listing)
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      notifications.toast.open({
        title: 'Bid Error',
        message: 'Unable to remove max bid. Please try again.',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        role: NOTIFICATION_ROLES.ERROR,
      })
      analytics.maxBid.trackRemoveAutoBidConfirm.fail(listing)
    } finally {
      close()
      cleanUp()
      setIsRemoveView(false)
      setModalCanAdjustOrRemove(false)
    }
  }, [segment.__methods__, user, listing, notifications, close, cleanUp])

  // defaulting these to true because if we don't get a restriction array back from graph, then don't lock down features
  const maxBiddingAllowed = (listing?.is_current_user_allowed || []).find(
    item =>
      item.action ===
      graph.enums.UserListingAuthorizationAction.OnlineProxyBidding // call the police, we called it proxy bid instead of max bid
  )?.allowed
  const regularBiddingAllowed = (listing?.is_current_user_allowed || []).find(
    item =>
      item.action ===
      graph.enums.UserListingAuthorizationAction.OnlineRegularBidding
  )?.allowed
  const isCurrentBiddingTypeRestricted = isMaxBiddingActive
    ? !maxBiddingAllowed
    : !regularBiddingAllowed

  const bidConfirmationModalProps = useMemo(
    () => ({
      children: (
        <BidConfirmationContent
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
          type={graph.enums.OnlineAuctionSegmentBidType.Regular}
          listing={listing}
          segment={segment}
          // @ts-ignore update DS type to use AuctionUser
          user={user}
          bidAmount={bidAmount}
          isPerformingBid={isPerformingBid}
          isOccupancyDisclaimerRequired={occupancyDisclaimerRequired}
          occupancyDisclaimerAcknowledged={occupancyAcknowledged}
          setOccupancyAcknowledged={setOccupancyAcknowledged}
        />
      ),
      elmId: 'pdp_bid_confirmation_modal',
      size: 'tiny',
      title: 'Confirm Bid',
      confirmButton: {
        onClick: performBid,
        disabled: isConfirmBidButtonDisabled(false),
      },
    }),
    [
      listing,
      segment,
      user,
      bidAmount,
      isPerformingBid,
      occupancyDisclaimerRequired,
      occupancyAcknowledged,
      performBid,
      isConfirmBidButtonDisabled,
    ]
  )

  const maxBidConfirmationModalProps = useMemo(() => {
    const getModalTitle = () => {
      if (isRemoveView) {
        return internationalization.remove_max_bid
      }
      if (isAdjustMode) {
        return internationalization.adjust_max_bid
      }
      return hasMaxBid
        ? internationalization.increase_max_bid
        : internationalization.set_max_bid
    }

    const getBidConfirmationContent = ({
      isRemoveConfirmation = false,
      canAdjustOrRemove = false,
    }: {
      isRemoveConfirmation?: boolean
      canAdjustOrRemove?: boolean
    }) => (
      <BidConfirmationContent
        type={graph.enums.OnlineAuctionSegmentBidType.MaxBid}
        listing={listing}
        segment={segment}
        // @ts-ignore update DS type to use AuctionUser
        user={user}
        bidAmount={bidAmount}
        isPerformingBid={isPerformingBid}
        isOccupancyDisclaimerRequired={occupancyDisclaimerRequired}
        occupancyDisclaimerAcknowledged={occupancyAcknowledged}
        setOccupancyAcknowledged={setOccupancyAcknowledged}
        handleBidAmountChange={value =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          value &&
          typeof value === 'number' &&
          onChange({
            target: { value: String(value) },
          } as React.ChangeEvent<HTMLInputElement>)
        }
        canAdjustOrRemove={canAdjustOrRemove}
        isRemoveConfirmation={isRemoveConfirmation}
      />
    )

    const sharedModalProps = {
      size: 'tiny',
      title: getModalTitle(),
      hideCloseButton: true,
      onclose: cleanUp,
    }

    if (isRemoveView) {
      return {
        elmId: 'pdp_max_bid_confirmation_remove_modal',
        ...sharedModalProps,
        children: getBidConfirmationContent({ isRemoveConfirmation: true }),
        confirmButton: {
          label: internationalization.remove,
          onClick: handleRemoveMaxBid,
          disabled: false,
        },
        cancelButton: {
          label: internationalization.keep_max_bid,
          onClick: () => setIsRemoveView(false),
          preventClose: true,
        },
        slots: {},
      }
    }

    return {
      elmId: 'pdp_max_bid_confirmation_modal',
      ...sharedModalProps,
      children: getBidConfirmationContent({
        canAdjustOrRemove: modalCanAdjustOrRemove,
      }),
      confirmButton: {
        label: internationalization.confirm_max_bid,
        onClick: performBid,
        disabled: isConfirmBidButtonDisabled(true),
      },
      cancelButton: {
        onClick: () => {
          close()
          cleanUp()
          setIsRemoveView(false)
          setModalCanAdjustOrRemove(false)
        },
      },
      ...(isAdjustMode && {
        slots: {
          actions: () => (
            <span
              style={{ color: colorVars.utkColorAction, cursor: 'pointer' }}
              onClick={() => {
                setIsRemoveView(true)
                analytics.maxBid.trackRemoveAutoBidClick(listing)
              }}
            >
              {internationalization.remove_max_bid}
            </span>
          ),
        },
      }),
    }
  }, [
    listing,
    segment,
    user,
    bidAmount,
    isPerformingBid,
    occupancyDisclaimerRequired,
    occupancyAcknowledged,
    hasMaxBid,
    performBid,
    isConfirmBidButtonDisabled,
    onChange,
    cleanUp,
    close,
    isRemoveView,
    isAdjustMode,
    modalCanAdjustOrRemove,
    handleRemoveMaxBid,
  ])

  useEffect(() => {
    if (biddingMode === graph.enums.OnlineAuctionSegmentBidType.Regular) {
      update(bidConfirmationModalProps)
    } else if (biddingMode === graph.enums.OnlineAuctionSegmentBidType.MaxBid) {
      update(maxBidConfirmationModalProps)
    }
  }, [
    bidConfirmationModalProps,
    biddingMode,
    maxBidConfirmationModalProps,
    update,
  ])

  const openBidConfirmationModal = useCallback(
    (buttonLabel: string) => {
      if (
        [
          MAX_BID_BUTTON_LABEL,
          INCREASE_MAX_BID_BUTTON_LABEL,
          ADJUST_MAX_BID_BUTTON_LABEL,
        ].includes(buttonLabel)
      ) {
        const isSetAutoBid = buttonLabel === MAX_BID_BUTTON_LABEL
        const isAdjustAutoBid = buttonLabel === ADJUST_MAX_BID_BUTTON_LABEL

        if (isAdjustAutoBid) {
          analytics.maxBid.trackAdjustAutoBidClick(listing)
        } else if (isSetAutoBid) {
          analytics.maxBid.trackSetAutoBidClick(listing)
        } else {
          analytics.maxBid.trackIncreaseAutoBidClick(listing)
        }

        setModalCanAdjustOrRemove(canAdjustOrRemove)
        setIsRemoveView(false)
        setIsAdjustMode(canAdjustOrRemove && hasMaxBid)
        setBiddingMode(graph.enums.OnlineAuctionSegmentBidType.MaxBid)
        resetBidAmountToZero()
        // Defer modal opening to next tick to ensure state updates complete, this is needed in case we had a valid max bid amount and the confirm button was enabled but on closing and re-opening, we don't want it to flash enabled then disabled as we're also resetting the amount back to 0
        requestAnimationFrame(() => {
          open('confirmation', maxBidConfirmationModalProps)
        })
      } else {
        setBiddingMode(graph.enums.OnlineAuctionSegmentBidType.Regular)
        open('confirmation', bidConfirmationModalProps)
      }
    },
    [
      setBiddingMode,
      resetBidAmountToZero,
      open,
      maxBidConfirmationModalProps,
      bidConfirmationModalProps,
      listing,
      canAdjustOrRemove,
      hasMaxBid,
      setIsAdjustMode,
    ]
  )

  const getMaxBidButtonLabel = useCallback(() => {
    if (hasMaxBid && canAdjustOrRemove) {
      return ADJUST_MAX_BID_BUTTON_LABEL
    }
    return hasMaxBid ? INCREASE_MAX_BID_BUTTON_LABEL : MAX_BID_BUTTON_LABEL
  }, [hasMaxBid, canAdjustOrRemove])

  const buttonLabel = useMemo(() => {
    const isOnlineAuctionActive = segment.__methods__.isOnlineAuctionActive()

    if (!isMaxBiddingActive) {
      return {
        primary: BID_BUTTON_LABEL,
        primaryDisabled: hasError || !bidAmount || !regularBiddingAllowed,
        primaryElmId: 'bid_button',
        secondary: '',
        secondaryDisabled: true,
        secondaryElmId: '',
      }
    }

    if (isOnlineAuctionActive) {
      return {
        primary: BID_BUTTON_LABEL,
        primaryDisabled: hasError || !bidAmount || !regularBiddingAllowed,
        primaryElmId: 'bid_button',
        secondary: getMaxBidButtonLabel(),
        secondaryDisabled: !maxBiddingAllowed,
        secondaryElmId: 'max_bid_button',
      }
    }

    // isMaxBiddingActive && !isOnlineAuctionActive
    return {
      primary: getMaxBidButtonLabel(),
      primaryDisabled: !maxBiddingAllowed,
      primaryElmId: 'max_bid_button',
      secondary: '',
      secondaryDisabled: true,
      secondaryElmId: '',
    }
  }, [
    isMaxBiddingActive,
    segment,
    hasError,
    bidAmount,
    regularBiddingAllowed,
    maxBiddingAllowed,
    getMaxBidButtonLabel,
  ])

  const isBiddingEnabled =
    isUserConfirmed && !isPendingFclOnlineDeposit(listing, featureFlags)

  return (
    <div className={classnames('u-stretch', containerClassName)}>
      {isCurrentBiddingTypeRestricted && (
        <AccountRestrictedAlert showIcon={false} />
      )}
      {!_.isEmpty(buttonLabel.primary) && (
        <Button
          role='primary'
          isStretch
          disabled={!isBiddingEnabled || buttonLabel.primaryDisabled}
          elmId={buttonLabel.primaryElmId}
          onClick={() =>
            handleBidButtonClick(openBidConfirmationModal(buttonLabel.primary))
          }
        >
          {buttonLabel.primary}
        </Button>
      )}
      {!_.isEmpty(buttonLabel.secondary) && (
        <Button
          role='secondary'
          isStretch
          disabled={!isBiddingEnabled || buttonLabel.secondaryDisabled}
          elmId={buttonLabel.secondaryElmId}
          onClick={() =>
            handleBidButtonClick(
              openBidConfirmationModal(buttonLabel.secondary)
            )
          }
        >
          {buttonLabel.secondary}
        </Button>
      )}
    </div>
  )
}

export default BidButton
